import React from 'react';
import { Grid, Section, Button } from '@boozallen-uip/uswds-react-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faBuilding } from '@fortawesome/free-solid-svg-icons';
import tw from 'tailwind-styled-components';
import PageHeader from '../../components/PageHeader/PageHeader.component';
import AboutWhy from './AboutWhy/AboutWhy.component';
import { SectionSubHeader } from '../shared.styled';
import PersistentDisclaimer from '../../components/disclaimer/PersistentDisclaimer';
import { BASE_TITLE, FOR_VENDORS_URL, FOR_BUYERS_URL } from '../../utlities/core.constants';
import './LandingPage.scss';

// A11y: UserTypeCard has min 44px touch target, keyboard focusable, proper semantic structure
const UserTypeCard = tw.div`user-type-card flex flex-col border-2 rounded min-h-[320px] sm:min-h-auto transition-all duration-300`;

export default function LandingPage() {
  document.title = `${BASE_TITLE} - Available for Early Use`;

  return (
    <>
      {/* A11y: Early Use Banner with role="alert" for screen reader announcement */}
      <div
        className="usa-alert usa-alert--warning"
        style={{ marginTop: 0, border: 0 }}
        role="alert"
        aria-live="polite"
      >
        <div className="usa-alert__body">
          <p className="usa-alert__text" style={{ fontSize: 16 }}>
            <strong className="usa-alert__heading" style={{ fontSize: 16 }}>ACR Repository Available for Early Use: </strong>
            The ACR Repository is ready for use while we await final PRA approval. Users may <strong>log in, request Vendor or Buyer access, and add ACRs</strong>. Broader promotion and the official launch will follow final PRA approval.
          </p>
        </div>
      </div>

      {/* Page Header Banner */}
      <PageHeader
        imageUrl="/page-banners/widescreen_hero_2x.webp"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundColor="var(--color-primary)"
      >
        <h1 className="m-0 font-normal text-white text-4xl leading-snug">The ACR Repository</h1>
      </PageHeader>

      {/* A11y: Main content landmark for screen readers and keyboard navigation */}
      <Section className="pt-0" id="landing_container" role="main" aria-label="Main content">
        <Grid>
          {/* Subheader */}
          <Grid.Row>
            <Grid.Col>
              <h2 className="text-primary-darker text-2xl font-normal margin-top-4 margin-bottom-3">
                Welcome to the ACR Repository
              </h2>
            </Grid.Col>
          </Grid.Row>

        {/* Main Content Section */}
        <Grid.Row>
          <Grid.Col>
            <p className="text-primary-darker text-base">
              The ACR Repository is a centralized library of Accessibility Conformance Reports (ACRs) submitted by ICT vendors. It helps federal agencies find, review, compare, and reuse accessibility documentation when researching products and planning acquisitions.
            </p>
            <p className="text-primary-darker text-base">
              Buyers can search for ACRs by product, vendor, or technology to better understand how a product conforms to Section 508 accessibility requirements before making purchasing decisions.
            </p>
            <p className="text-primary-darker text-base">
              Vendors can create, publish, and maintain their ACRs in one authoritative location, making them easier for federal agencies and other customers to discover and evaluate.
            </p>
            <p className="text-primary-darker text-base margin-bottom-4">
              By providing a common repository for ACRs, the ACR Repository increases transparency, reduces duplicate requests for accessibility documentation, and supports more informed, efficient procurement decisions.
            </p>
          </Grid.Col>
        </Grid.Row>

        {/* ACR Repository Value Section */}
        <Grid.Row>
          <Grid.Col>
            <SectionSubHeader className="mt-0">ACR Repository brings accessibility documentation to the forefront of procurement</SectionSubHeader>
            <AboutWhy />
          </Grid.Col>
        </Grid.Row>

        {/* User Type Cards Section */}
        <Grid.Row gaps="true" gapSize="2" className="margin-top-4 margin-bottom-4 max-w-none">
          <Grid.Col tablet={6} className="flex px-0">
            {/* A11y: Informational card with a single interactive element (the CTA button) */}
            <UserTypeCard
              className="vendor-card shadow-lg w-full"
              role="region"
              aria-labelledby="vendor-heading"
              aria-describedby="vendor-desc"
            >
              {/* A11y: Visually hidden text for screen reader context */}
              <span className="usa-sr-only" id="vendor-desc">
                Information for vendors and sellers about using the ACR Repository to manage product accessibility documentation
              </span>
              {/* Full-bleed icon header */}
              <div className="card-header bg-primary flex items-center justify-center" style={{ minHeight: '70px' }} aria-hidden="true">
                <FontAwesomeIcon icon={faStore} className="text-white" style={{ fontSize: '40px' }} />
              </div>
              {/* Content section */}
              <div className="card-content bg-white flex flex-col flex-1" style={{ padding: '1.25rem' }}>
                <h2 id="vendor-heading" className="text-primary-darker font-bold margin-top-0" style={{ fontSize: '1.5rem', marginBottom: '0.375rem' }}>
                  Vendors/Sellers
                </h2>
                <p className="text-primary-darker font-semibold" style={{ fontSize: '0.9375rem', marginBottom: '0.75rem', lineHeight: '1.4' }}>
                  Manage your product accessibility documentation
                </p>
                <div className="flex-1">
                  <ol className="custom-numbered-list text-primary-darker" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }} aria-label="Steps for vendors">
                    <li aria-label="Step 1 of 4: Create a vendor profile and list your products">Create a vendor profile and list your products.</li>
                    <li aria-label="Step 2 of 4: Use the ACR Editor or upload existing ACRs">Use the ACR Editor or upload existing ACRs.</li>
                    <li aria-label="Step 3 of 4: Provide Section 508 conformance information upfront">Provide Section 508 conformance information upfront.</li>
                    <li aria-label="Step 4 of 4: Reduce back-and-forth with procurement teams">Reduce back-and-forth with procurement teams.</li>
                  </ol>
                  <p className="text-primary-darker" style={{ fontSize: '0.875rem', lineHeight: '1.5', marginBottom: '0.75rem' }}>
                    Create and submit your products in a centralized location so customers can easily access your ACRs. Build trust with the disability community and enhance customer satisfaction with high-quality accessibility documentation.
                  </p>
                </div>
                <div className="card-cta margin-top-2">
                  <Button href={FOR_VENDORS_URL}>
                    For Vendors
                  </Button>
                </div>
              </div>
            </UserTypeCard>
          </Grid.Col>
          <Grid.Col tablet={6} className="flex px-0">
            {/* A11y: Informational card with a single interactive element (the CTA button) */}
            <UserTypeCard
              className="buyer-card shadow-lg w-full"
              role="region"
              aria-labelledby="buyer-heading"
              aria-describedby="buyer-desc"
            >
              {/* A11y: Visually hidden text for screen reader context */}
              <span className="usa-sr-only" id="buyer-desc">
                Information for government buyers about using the ACR Repository to access and evaluate product accessibility reports
              </span>
              {/* Full-bleed icon header */}
              <div className="card-header bg-primary-light flex items-center justify-center" style={{ minHeight: '70px' }} aria-hidden="true">
                <FontAwesomeIcon icon={faBuilding} className="text-white" style={{ fontSize: '40px' }} />
              </div>
              {/* Content section */}
              <div className="card-content bg-white flex flex-col flex-1" style={{ padding: '1.25rem' }}>
                <h2 id="buyer-heading" className="text-primary-darker font-bold margin-top-0" style={{ fontSize: '1.5rem', marginBottom: '0.375rem' }}>
                  Government Buyers
                </h2>
                <p className="text-primary-darker font-semibold" style={{ fontSize: '0.9375rem', marginBottom: '0.75rem', lineHeight: '1.4' }}>
                  Access and evaluate product accessibility reports
                </p>
                <div className="flex-1">
                  <ol className="custom-numbered-list text-primary-darker" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }} aria-label="Steps for government buyers">
                    <li aria-label="Step 1 of 4: Create a government account to access ACRs">Create a government account to access ACRs.</li>
                    <li aria-label="Step 2 of 4: Search products by accessibility conformance">Search products by accessibility conformance.</li>
                    <li aria-label="Step 3 of 4: Compare accessibility of similar products">Compare accessibility of similar products.</li>
                    <li aria-label="Step 4 of 4: Request additional information from vendors">Request additional information from vendors.</li>
                  </ol>
                  <p className="text-primary-darker" style={{ fontSize: '0.875rem', lineHeight: '1.5', marginBottom: '0.75rem' }}>
                    Easily search and access comprehensive ACRs to understand Section 508 conformance of products being considered for purchase. Make informed procurement decisions with standardized accessibility information.
                  </p>
                </div>
                <div className="card-cta margin-top-2">
                  <Button href={FOR_BUYERS_URL}>
                    For Buyers
                  </Button>
                </div>
              </div>
            </UserTypeCard>
          </Grid.Col>
        </Grid.Row>

        {/* A11y: Contact Section as complementary landmark */}
        <Grid.Row className="bg-primary-darker padding-4 margin-top-4">
          <Grid.Col className="text-center" role="complementary" aria-labelledby="contact-heading">
            <h2 id="contact-heading" className="text-white text-2xl font-bold margin-bottom-1">Contact Us</h2>
            <p className="text-white text-base">
              All questions about the ACR Repository can be directed to{' '}
              <a
                href="mailto:acr.repository@gsa.gov"
                className="text-white"
                aria-label="Email acr.repository@gsa.gov for questions about ACR Repository"
              >
                acr.repository@gsa.gov
              </a>
              .
            </p>
          </Grid.Col>
        </Grid.Row>

        {/* Disclaimer */}
        <Grid.Row className="mt-10 mb-0">
          <Grid.Col>
            <PersistentDisclaimer
              storageKey="acr_long_disclaimer_collapsed"
              title="Disclaimer"
            >
              <p className="mt-0">
                The information provided on the ACR Repository (staging-acrrepo.section508.gov) is made available for general informational purposes only. The Accessibility Conformance Reports (ACRs) and related content accessible through this site are provided on an &quot;as is&quot; basis, without warranty or guarantee of any kind, express or implied.
              </p>
              <p>
                The General Services Administration (GSA) and contributing agencies do not warrant or represent that the information contained herein is complete, accurate, current, or free from error. ACRs are submitted by vendors and agencies and reflect conditions at the time of their creation; they may not reflect subsequent changes to products, technologies, or accessibility standards.
              </p>
              <p>
                Users should not rely solely on the information provided through this repository when making procurement, compliance, or accessibility determinations. Independent verification is strongly encouraged. GSA assumes no liability for decisions made based on the content of this site.
              </p>
              <p className="mb-0">
                Reference to any specific product, vendor, or report does not constitute endorsement or recommendation by the U.S. federal government.
              </p>
            </PersistentDisclaimer>
          </Grid.Col>
        </Grid.Row>
        </Grid>
      </Section>
    </>
  );
}
